import type { Venue } from '../types';
import { formatCategory, getVenueCategories } from './venue-categories';
import {
  isVenueClosed,
  scanFreshnessOf,
  venueConfidenceBucket,
  venueSocialBucket,
} from './venueFilters';

/** A single grouping level. The available keys differ between public and admin
 *  pages — pass the appropriate options array to {@link GroupBySelector}. */
export type VenueGroupByKey =
  | 'neighborhood'
  | 'category'
  | 'social'
  | 'active'
  | 'confidence'
  | 'scan'
  | 'firstLetter';

export interface VenueGroupByOption {
  key: VenueGroupByKey;
  label: string;
}

/** Public Browse page grouping options. */
export const PUBLIC_VENUE_GROUP_BY_OPTIONS: VenueGroupByOption[] = [
  { key: 'neighborhood', label: 'Neighborhood' },
  { key: 'category', label: 'Category' },
  { key: 'social', label: 'Social presence' },
];

/** Admin Manage page grouping options (super-set of public). */
export const ADMIN_VENUE_GROUP_BY_OPTIONS: VenueGroupByOption[] = [
  { key: 'neighborhood', label: 'Neighborhood' },
  { key: 'category', label: 'Category' },
  { key: 'social', label: 'Social presence' },
  { key: 'active', label: 'Active status' },
  { key: 'confidence', label: 'Confidence' },
  { key: 'scan', label: 'Scan freshness' },
];

const ALL_VENUE_GROUP_KEYS = new Set<VenueGroupByKey>([
  'neighborhood', 'category', 'social', 'active', 'confidence', 'scan', 'firstLetter',
]);

const NONE_VALUE = '__none__';

export interface VenueGroupNode {
  key: VenueGroupByKey;
  value: string;
  label: string;
  count: number;
  children: VenueGroupNode[];
  venues: Venue[];
}

export function groupVenues(venues: Venue[], keys: VenueGroupByKey[]): VenueGroupNode[] {
  if (keys.length === 0) {
    return [
      {
        key: 'neighborhood',
        value: '__all__',
        label: 'All venues',
        count: venues.length,
        children: [],
        venues,
      },
    ];
  }
  return groupRecursive(venues, keys, 0);
}

function groupRecursive(
  venues: Venue[],
  keys: VenueGroupByKey[],
  depth: number,
): VenueGroupNode[] {
  const key = keys[depth];
  // category groups produce one bucket per category; a venue with multiple
  // categories belongs to all of its buckets so the user can find it under
  // any of them. Other keys produce a single bucket per venue.
  const buckets = new Map<string, { label: string; venues: Venue[] }>();
  for (const v of venues) {
    for (const { value, label } of bucketsFor(v, key)) {
      const existing = buckets.get(value);
      if (existing) existing.venues.push(v);
      else buckets.set(value, { label, venues: [v] });
    }
  }
  const nodes: VenueGroupNode[] = [];
  for (const [value, { label, venues: bucketVenues }] of buckets) {
    const children =
      depth + 1 < keys.length
        ? groupRecursive(bucketVenues, keys, depth + 1)
        : [];
    nodes.push({
      key,
      value,
      label,
      count: bucketVenues.length,
      children,
      venues: children.length > 0 ? [] : bucketVenues,
    });
  }
  sortGroupNodes(nodes, key);
  return nodes;
}

function bucketsFor(v: Venue, key: VenueGroupByKey): Array<{ value: string; label: string }> {
  switch (key) {
    case 'neighborhood': {
      const n = v.partitionKey?.trim();
      return n ? [{ value: n, label: n }] : [{ value: NONE_VALUE, label: '(No neighborhood)' }];
    }
    case 'category': {
      const cats = getVenueCategories(v);
      if (!cats.length) return [{ value: NONE_VALUE, label: '(Uncategorized)' }];
      return cats.map((c) => ({ value: c, label: formatCategory(c) }));
    }
    case 'social': {
      const b = venueSocialBucket(v);
      const labels: Record<typeof b, string> = {
        both: 'Instagram + Facebook',
        ig: 'Instagram only',
        fb: 'Facebook only',
        none: '(No social)',
      };
      return [{ value: b, label: labels[b] }];
    }
    case 'active': {
      return v.active === false
        ? [{ value: 'inactive', label: 'Inactive' }]
        : [{ value: 'active', label: 'Active' }];
    }
    case 'confidence': {
      if (isVenueClosed(v)) {
        return [{ value: 'closed', label: 'Closed' }];
      }
      const b = venueConfidenceBucket(v.venueConfidence);
      const labels: Record<typeof b, string> = {
        high: 'High confidence (≥70)',
        medium: 'Medium confidence (40–69)',
        low: 'Low confidence (<40)',
        unknown: '(No confidence score)',
      };
      return [{ value: b, label: labels[b] }];
    }
    case 'scan': {
      const f = scanFreshnessOf(v);
      const labels: Record<typeof f, string> = {
        recent: 'Scanned recently',
        stale: 'Scan stale (>14d)',
        never: 'Never scanned',
      };
      return [{ value: f, label: labels[f] }];
    }
    case 'firstLetter': {
      const first = (v.name || '').trim().charAt(0).toUpperCase();
      if (!first) return [{ value: NONE_VALUE, label: '(No name)' }];
      if (!/[A-Z]/.test(first)) return [{ value: '#', label: '#' }];
      return [{ value: first, label: first }];
    }
  }
}

const SORT_ORDER: Partial<Record<VenueGroupByKey, string[]>> = {
  social: ['both', 'ig', 'fb', 'none'],
  active: ['active', 'inactive'],
  confidence: ['high', 'medium', 'low', 'unknown', 'closed'],
  scan: ['recent', 'stale', 'never'],
};

function sortGroupNodes(nodes: VenueGroupNode[], key: VenueGroupByKey): void {
  const order = SORT_ORDER[key];
  if (order) {
    const idx = (v: string) => {
      const i = order.indexOf(v);
      return i === -1 ? order.length : i;
    };
    nodes.sort((a, b) => idx(a.value) - idx(b.value));
    return;
  }
  nodes.sort((a, b) => {
    if (a.value === NONE_VALUE && b.value !== NONE_VALUE) return 1;
    if (b.value === NONE_VALUE && a.value !== NONE_VALUE) return -1;
    return a.label.localeCompare(b.label);
  });
}

/** Serialise grouping to a comma-separated URL value. */
export function venueGroupingToParam(keys: VenueGroupByKey[]): string | undefined {
  return keys.length ? keys.join(',') : undefined;
}

export function venueGroupingFromParam(value: string | null | undefined): VenueGroupByKey[] {
  if (!value) return [];
  const seen = new Set<VenueGroupByKey>();
  const out: VenueGroupByKey[] = [];
  for (const raw of value.split(',')) {
    const trimmed = raw.trim() as VenueGroupByKey;
    if (!ALL_VENUE_GROUP_KEYS.has(trimmed) || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

const LABEL_BY_KEY: Record<VenueGroupByKey, string> = {
  neighborhood: 'Neighborhood',
  category: 'Category',
  social: 'Social presence',
  active: 'Active status',
  confidence: 'Confidence',
  scan: 'Scan freshness',
  firstLetter: 'First letter',
};

export function venueGroupByLabel(key: VenueGroupByKey): string {
  return LABEL_BY_KEY[key];
}
